import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Map as MapLibreMap, type StyleSpecification } from "maplibre-gl";
import { AiOutlineZoomIn, AiOutlineZoomOut } from "react-icons/ai";
import { MapProvider } from "@/context/mapContext";
import { useMapContext } from "@/hooks/useMap";
import { TaskProvider, useTaskContext } from "@/context/taskContext";
import { useModalStore } from "@/stores/modalStore";
import {
  IconBar,
  LayersSwitcher,
  TaskLayer,
  AddLayersManager,
} from "@/components/map";
import { HighlightLayer } from "@/components/map/HighlightLayer";

import { TaskTable } from "@/components/table";
import { TaskForm, GeometrySelectionModal } from "@/components/forms";
import { TopNavbar } from "@/components/layout";
import { Toaster } from "react-hot-toast";
import type { Task, TaskGeometry } from "@/types/task";
import { useBasemaps } from "@/hooks/useBasemaps";
import "maplibre-gl/dist/maplibre-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

const MAP_BOUNDS_STORAGE_KEY = "taskr-map-bounds";
const DEFAULT_CENTER: [number, number] = [-82.9962, 40.3675]; // lng, lat
const DEFAULT_ZOOM = 7;

type StoredBounds = {
  center: [number, number]; // stored as [lat, lng] for backward compatibility
  zoom: number;
};

const toMapLibreCenter = (center: [number, number]): [number, number] => {
  const [first, second] = center;
  if (Math.abs(first) <= 90 && Math.abs(second) <= 180) {
    return [second, first];
  }
  return center;
};

function MapPageInner() {
  const {
    mapInstance,
    registerMapInstance,
    unregisterMapInstance,
    tableVisible,
    setTableVisible,
    showBasemapSwitcher,
    setShowBasemapSwitcher,
    startDrawing: startContextDrawing,
    stopDrawing: stopContextDrawing,
    clearDrawnGeometry,
    setOnGeometryDrawn,
    selectedBasemapKey,
    isDrawing,
    clearHighlight,
  } = useMapContext();

  // Load basemaps from database
  const { visibleBasemaps } = useBasemaps();

  const { selectTask, refreshTask, tasks } = useTaskContext();
  const {
    editingTask,
    viewingTask,
    showCreateTask,
    showGeometrySelection,
    showCreateLayer,
    editingLayer,
    closeAllModals,
    openCreateTask,
    openEditTask,
    openViewTask,
    openGeometrySelection,
    closeCreateTask,
    closeEditTask,
    closeViewTask,
    closeGeometrySelection,
  } = useModalStore();
  const location = useLocation();
  const [drawnGeometry, setDrawnGeometry] = useState<TaskGeometry | null>(null);

  // State to control which style is passed to the Map component
  // Initialize with a minimal StyleSpecification that includes glyphs/sprite
  // so libraries that add symbol layers with text-field (e.g. Mapbox Draw) won't fail
  const [currentMapStyle, setCurrentMapStyle] = useState<
    StyleSpecification | string
  >(() => ({
    version: 8,
    name: "base",
    glyphs: "https://tiles.basemaps.cartocdn.com/fonts/{fontstack}/{range}.pbf",
    sources: {},
    layers: [
      {
        id: "background",
        type: "background",
        paint: { "background-color": "#f8fafc" },
      },
    ],
  }));

  const [layerTaskData, setLayerTaskData] = useState<{
    lat: string;
    lng: string;
    layerName: string;
    asset_field: string;
    asset_id: string;
    geometry?: TaskGeometry;
    [key: string]: unknown;
  } | null>(null);

  // Get initial map bounds from sessionStorage
  const getInitialMapBounds = useCallback((): {
    center: [number, number];
    zoom: number;
  } => {
    try {
      const savedBounds = sessionStorage.getItem(MAP_BOUNDS_STORAGE_KEY);
      if (savedBounds) {
        const bounds: Partial<StoredBounds> = JSON.parse(savedBounds);
        if (
          bounds &&
          Array.isArray(bounds.center) &&
          bounds.center.length === 2 &&
          typeof bounds.zoom === "number"
        ) {
          return {
            center: toMapLibreCenter(bounds.center as [number, number]),
            zoom: bounds.zoom,
          };
        }
      }
    } catch (error) {
      console.warn("Error loading map bounds from sessionStorage:", error);
    }

    return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
  }, []);

  // Save map bounds to sessionStorage
  const saveMapBounds = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    try {
      const center = map.getCenter();
      const zoom = map.getZoom();
      const bounds: StoredBounds = {
        center: [center.lat, center.lng],
        zoom,
      };
      sessionStorage.setItem(MAP_BOUNDS_STORAGE_KEY, JSON.stringify(bounds));
    } catch (error) {
      console.warn("Error saving map bounds to sessionStorage:", error);
    }
  }, [mapInstance]);

  const applyBasemap = useCallback(
    (_map: MapLibreMap, basemapKey: string) => {
      const basemap =
        visibleBasemaps.find((b) => b.key === basemapKey) ?? visibleBasemaps[0];
      if (basemap && basemap.type === "vector") {
        setCurrentMapStyle(basemap.styleUrl);
      }
    },
    [visibleBasemaps]
  );

  // Map initialization is now handled by react-map-gl's Map component
  // See handleMapLoad callback which is called when the map is ready

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unregisterMapInstance();
    };
  }, [unregisterMapInstance]);

  // Handle basemap style changes (vector vs raster)
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const basemap =
      visibleBasemaps.find((b) => b.key === selectedBasemapKey) ??
      visibleBasemaps[0];

    if (!basemap) return;

    // Switch to vector style URL - Map component will load it
    if (basemap.type === "vector") {
      setCurrentMapStyle(basemap.styleUrl);

      // Wait for the new style to load, then emit basemap-changed event
      const handleStyleData = () => {
        if (map.isStyleLoaded?.()) {
          console.log(
            `[MapPage] Style loaded, emitting basemap-changed for ${selectedBasemapKey}`
          );
          window.dispatchEvent(
            new CustomEvent("basemap-changed", {
              detail: { basemapKey: selectedBasemapKey },
            })
          );
          map.off("styledata", handleStyleData);
        }
      };

      map.on("styledata", handleStyleData);

      // Fallback timeout to ensure event is emitted even if styledata doesn't fire
      const fallbackTimeout = window.setTimeout(() => {
        console.log(
          `[MapPage] Fallback timeout, emitting basemap-changed for ${selectedBasemapKey}`
        );
        map.off("styledata", handleStyleData);
        window.dispatchEvent(
          new CustomEvent("basemap-changed", {
            detail: { basemapKey: selectedBasemapKey },
          })
        );
      }, 2000);

      return () => {
        map.off("styledata", handleStyleData);
        window.clearTimeout(fallbackTimeout);
      };
    }
  }, [selectedBasemapKey, mapInstance]);

  // Restore map bounds when returning to map tab
  useEffect(() => {
    const currentPath = location.pathname;
    const isMapRoute = currentPath === "/map" || currentPath === "/";

    if (isMapRoute && mapInstance.current) {
      // Small delay to ensure map is fully loaded
      const timer = setTimeout(() => {
        try {
          const map = mapInstance.current;
          if (!map) return;
          const savedBounds = sessionStorage.getItem(MAP_BOUNDS_STORAGE_KEY);
          if (savedBounds) {
            const bounds = JSON.parse(savedBounds);
            if (
              bounds &&
              bounds.center &&
              bounds.zoom &&
              Array.isArray(bounds.center) &&
              bounds.center.length === 2
            ) {
              map.flyTo({
                center: toMapLibreCenter(bounds.center as [number, number]),
                zoom: bounds.zoom,
                essential: false,
              });
            }
          }
        } catch (error) {
          console.warn(
            "Error restoring map bounds from sessionStorage:",
            error
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, mapInstance]);

  // Centralized closer for all popups/overlays
  const closeAllOverlays = useCallback(() => {
    // Close any modals and dropdowns managed by modalStore
    closeAllModals();
    // Hide task table and layer switcher
    setTableVisible(false);
    setShowBasemapSwitcher(false);
    // Clear highlight when closing overlays
    clearHighlight();
  }, [closeAllModals, setTableVisible, setShowBasemapSwitcher, clearHighlight]);

  // Helper to close any task-related modals in one place (used in multiple spots)
  const closeTaskModals = useCallback(() => {
    try {
      closeCreateTask();
    } catch {
      /* ignore */
    }
    try {
      closeEditTask();
    } catch {
      /* ignore */
    }
    try {
      closeViewTask();
    } catch {
      /* ignore */
    }
  }, [closeCreateTask, closeEditTask, closeViewTask]);

  // Define handleViewTask early so it can be used in useEffect
  const handleViewTask = useCallback(
    (task: Task) => {
      // Close modals and popups but keep task table open
      closeAllModals();
      setShowBasemapSwitcher(false);
      openViewTask(task);
      selectTask(task);
    },
    [selectTask, openViewTask, closeAllModals, setShowBasemapSwitcher]
  );

  // Listen for unified popup "View Task" button clicks
  useEffect(() => {
    const handleUnifiedPopupViewTask = (e: CustomEvent<{ task: Task }>) => {
      if (e.detail?.task) {
        handleViewTask(e.detail.task);
      }
    };

    window.addEventListener(
      "unified-popup-view-task",
      handleUnifiedPopupViewTask as EventListener
    );

    return () => {
      window.removeEventListener(
        "unified-popup-view-task",
        handleUnifiedPopupViewTask as EventListener
      );
    };
  }, [handleViewTask]);

  // Set up global click handler for task popup buttons
  useEffect(() => {
    const handleTaskPopupClick = (taskId: string) => {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        handleViewTask(task);
      }
    };

    // Make the handler available globally for popup buttons
    (
      window as unknown as { taskLayerClickHandler: (taskId: string) => void }
    ).taskLayerClickHandler = handleTaskPopupClick;

    return () => {
      // Cleanup
      delete (
        window as unknown as {
          taskLayerClickHandler?: (taskId: string) => void;
        }
      ).taskLayerClickHandler;
    };
  }, [tasks, handleViewTask]);

  // Close TaskForm when layer controller opens
  useEffect(() => {
    if (showBasemapSwitcher && (showCreateTask || editingTask || viewingTask)) {
      closeTaskModals();
    }
  }, [
    showBasemapSwitcher,
    showCreateTask,
    editingTask,
    viewingTask,
    closeCreateTask,
    closeEditTask,
    closeViewTask,
    closeTaskModals,
  ]);

  // Close TaskForm when layer manager modals open
  useEffect(() => {
    if (
      (showCreateLayer || editingLayer) &&
      (showCreateTask || editingTask || viewingTask)
    ) {
      closeTaskModals();
    }
  }, [
    showCreateLayer,
    editingLayer,
    showCreateTask,
    editingTask,
    viewingTask,
    closeCreateTask,
    closeEditTask,
    closeViewTask,
    closeTaskModals,
  ]);

  // Listen for add task requests from IconBar
  useEffect(() => {
    const handleAddTaskRequest = () => {
      // Show geometry selection modal
      openGeometrySelection();
    };

    window.addEventListener(
      "addTaskRequest",
      handleAddTaskRequest as EventListener
    );

    return () => {
      window.removeEventListener(
        "addTaskRequest",
        handleAddTaskRequest as EventListener
      );
    };
  }, [openGeometrySelection]);

  // Handle escape key to cancel drawing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isDrawing) {
        stopContextDrawing();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [stopContextDrawing, isDrawing]);

  const handleCreateTask = () => {
    // Ensure all other popups are closed before starting create flow
    closeAllOverlays();
    openGeometrySelection();
  };

  const handleAddTaskFromLayer = (taskData: {
    lat: string;
    lng: string;
    layerName: string;
    asset_field: string;
    asset_id: string;
    geometry?: TaskGeometry;
    [key: string]: unknown;
  }) => {
    // Ensure all other popups are closed before opening create form
    closeAllOverlays();
    // Use the feature geometry if provided, otherwise create a point at the clicked location
    let geometry;
    if (taskData.geometry) {
      geometry = taskData.geometry;
    } else {
      // Fallback to point geometry at the clicked coordinates
      geometry = {
        type: "Point" as const,
        coordinates: [parseFloat(taskData.lng), parseFloat(taskData.lat)],
      };
    }

    // Store the layer task data for later use in the form
    setLayerTaskData(taskData);

    // Set the geometry and open the task form directly
    setDrawnGeometry(geometry);
    openCreateTask();
  };

  const handleGeometryTypeSelected = (type: "point" | "line" | "polygon") => {
    closeGeometrySelection();

    // Set up callback for when geometry is drawn
    setOnGeometryDrawn((geometry: TaskGeometry) => {
      handleGeometryDrawn(geometry);
    });

    // Use MapContext drawing with 'layer' source (for consistency)
    startContextDrawing(type, "layer");
  };

  const handleGeometryDrawn = (geometry: TaskGeometry) => {
    setDrawnGeometry(geometry);

    // Clear the callback to prevent multiple triggers
    setOnGeometryDrawn(undefined);

    // Disable drawing mode
    stopContextDrawing();

    // Show task form with the drawn geometry
    openCreateTask();
  };

  const handleTaskSelect = (task: Task | null) => {
    selectTask(task);
  };

  const handleTaskSuccess = async (task: Task) => {
    // Refresh the task data to get latest information
    await refreshTask(task.id);
    // Clear the drawn geometry after successful task creation
    setDrawnGeometry(null);
    clearDrawnGeometry();
  };

  // Wrapper for TaskForm onSuccess callback
  const handleTaskFormSuccess = () => {
    if (editingTask) {
      handleTaskSuccess(editingTask);
    }
    closeEditTask();
    closeCreateTask();
  };

  // Update cursor when drawing state changes
  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;

    const canvas = map.getCanvas();
    if (isDrawing) {
      // Use default pointer when drawing and keep it throughout
      canvas.style.cursor = "default";
      // Set a flag so mousedown/mouseup handlers know not to change cursor
      (window as { __isDrawingActive?: boolean }).__isDrawingActive = true;
    } else {
      // Reset to empty (will be controlled by hover)
      canvas.style.cursor = "";
      (window as { __isDrawingActive?: boolean }).__isDrawingActive = false;
    }
  }, [isDrawing, mapInstance]);

  // Handle editing a task from view mode
  const handleEditFromView = (task: Task) => {
    closeViewTask(); // Close view mode
    openEditTask(task); // Open edit mode
  };

  // Zoom control handlers
  const handleZoomIn = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomIn();
    }
  }, [mapInstance]);

  const handleZoomOut = useCallback(() => {
    if (mapInstance.current) {
      mapInstance.current.zoomOut();
    }
  }, [mapInstance]);

  // Initialize map on mount
  useEffect(() => {
    const mapContainer = document.getElementById("map-container");
    if (!mapContainer || mapInstance.current) return;

    const { center, zoom } = getInitialMapBounds();

    const map = new MapLibreMap({
      container: mapContainer,
      style: currentMapStyle as StyleSpecification,
      center: center,
      zoom: zoom,
      attributionControl: false,
    });

    registerMapInstance(map);
    applyBasemap(map, selectedBasemapKey);

    // Override default grab cursor on drag
    const canvas = map.getCanvas();
    canvas.style.cursor = "default";
    map.on("mousedown", () => {
      // Don't change cursor during drawing
      const drawingActive = (window as { __isDrawingActive?: boolean })
        .__isDrawingActive;
      if (!drawingActive) {
        canvas.style.cursor = "move";
      }
    });

    map.on("mouseup", () => {
      // Don't reset cursor during drawing
      const drawingActive = (window as { __isDrawingActive?: boolean })
        .__isDrawingActive;
      if (!drawingActive) {
        canvas.style.cursor = "default";
      }
    });

    // Save bounds on move
    map.on("moveend", () => {
      saveMapBounds();
    });

    return () => {
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map style when basemap changes
  useEffect(() => {
    if (mapInstance.current && currentMapStyle) {
      mapInstance.current.setStyle(currentMapStyle as StyleSpecification);
    }
  }, [currentMapStyle, mapInstance]);

  return (
    <div className="w-screen h-screen relative">
      {/* Top Navigation Bar */}
      <div className="absolute top-0 w-full z-1001">
        <TopNavbar />
      </div>

      {/* Map Container */}
      <div id="map-container" className="w-screen h-screen z-10" />

      {/* Task Layer */}
      <TaskLayer />

      {/* Add Layers */}
      <AddLayersManager
        onAddTask={handleAddTaskFromLayer}
        onShowGeometrySelection={handleAddTaskFromLayer}
        onLoadingStart={() => {
          // Close TaskForm when ArcGIS layer popup starts loading
          if (showCreateTask || editingTask || viewingTask) {
            closeTaskModals();
          }
        }}
        onLoadingEnd={() => {}}
      />

      {/* Highlight Layer - renders highlighted features on top */}
      <HighlightLayer />

      {/* Components outside the map */}

      {/* Loading Overlay - DISABLED */}
      {/* {isLoadingPopup && <Loader />} */}
      {/* Task Table Overlay */}
      {tableVisible && (
        <div
          className="absolute left-24 bottom-4 h-100 z-1000 rounded-lg"
          style={{ width: "calc(100vw - 220px)" }}
        >
          {/* Close Button */}
          <div className="h-100">
            <TaskTable
              onCreateTask={handleCreateTask}
              onViewTask={handleViewTask}
              onTaskSelect={handleTaskSelect}
            />
          </div>
        </div>
      )}

      {/* Drawing Status Indicator */}
      {isDrawing && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-2000 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">
              Click on the map to draw your shape
            </span>
            <button
              onClick={() => {
                stopContextDrawing();
              }}
              className="text-white hover:text-gray-200 text-sm underline"
            >
              Cancel (ESC)
            </button>
          </div>
        </div>
      )}

      {/* Icon Bar */}
      <IconBar />

      {/* Basemap Switcher */}
      <LayersSwitcher />

      {/* Zoom Controls */}
      <div className="absolute bottom-4 left-4 z-1000 p-1 bg-white rounded-xl shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] inline-flex flex-col justify-start items-start">
        <button onClick={handleZoomIn} className="" title="Zoom In">
          <AiOutlineZoomIn
            size={26}
            className="w-14 h-14 p-3.5 bg-white rounded-xl inline-flex justify-center items-center gap-2.5 hover:bg-gray-100 hover:cursor-pointer custom-text-gray-dark custom-text-dark-gray-hover"
          />
        </button>
        <button onClick={handleZoomOut} className="" title="Zoom Out">
          <AiOutlineZoomOut
            size={26}
            className="w-14 h-14 p-3.5 bg-white rounded-xl inline-flex justify-center items-center gap-2.5 hover:bg-gray-100 hover:cursor-pointer custom-text-gray-dark custom-text-dark-gray-hover"
          />
        </button>
      </div>

      {/* Geometry Selection Modal */}
      {showGeometrySelection && (
        <GeometrySelectionModal
          onClose={() => {
            closeGeometrySelection();
            // Cancel any active drawing
            stopContextDrawing();
          }}
          onGeometryTypeSelected={handleGeometryTypeSelected}
        />
      )}

      {/* Task Creation Modal */}
      {showCreateTask && (
        <TaskForm
          isOpen={true}
          onClose={() => {
            closeCreateTask();
            setDrawnGeometry(null);
            setLayerTaskData(null);
            clearDrawnGeometry();
          }}
          onSuccess={handleTaskFormSuccess}
          initialGeometry={drawnGeometry || undefined}
          layerData={
            layerTaskData
              ? {
                  layerName: layerTaskData.layerName,
                  asset_field: layerTaskData.asset_field,
                  asset_id: layerTaskData.asset_id,
                }
              : undefined
          }
        />
      )}

      {/* Task Viewing Modal */}
      {(viewingTask || editingTask) && (
        <TaskForm
          isOpen={true}
          mode={viewingTask ? "view" : "edit"}
          task={viewingTask || editingTask || undefined}
          onClose={() => (viewingTask ? closeViewTask() : closeEditTask())}
          onEditTask={viewingTask ? handleEditFromView : undefined}
          onSuccess={editingTask ? handleTaskFormSuccess : undefined}
        />
      )}

      {/* Toast Notifications */}
      <Toaster position="bottom-center" />
    </div>
  );
}

export function MapPage() {
  return (
    <TaskProvider>
      <MapProvider>
        <MapPageInner />
      </MapProvider>
    </TaskProvider>
  );
}
